/**
 * @license agm-direction
 * MIT license
 */

import { __decorate, __metadata } from 'tslib';
import { EventEmitter, Input, Output, Directive, NgModule } from '@angular/core';
import { GoogleMapsAPIWrapper } from '@agm/core';

let AgmDirection = class AgmDirection {
    constructor(gmapsApi) {
        this.gmapsApi = gmapsApi;
        // Options
        this.travelMode = 'DRIVING';
        this.transitOptions = undefined;
        this.drivingOptions = undefined;
        this.waypoints = [];
        this.optimizeWaypoints = true;
        this.provideRouteAlternatives = false;
        this.avoidHighways = false;
        this.avoidTolls = false;
        // Remove or draw direction
        this.visible = true;
        // Direction change event handler
        this.onChange = new EventEmitter();
        // Direction response for the new request
        this.onResponse = new EventEmitter();
        // Send a custom infowindow
        this.sendInfoWindow = new EventEmitter();
        // Status of Directions Query (google.maps.DirectionsStatus.OVER_QUERY_LIMIT)
        this.status = new EventEmitter();
        // Marker drag event handler
        this.originDrag = new EventEmitter();
        this.destinationDrag = new EventEmitter();
        this.directionsService = undefined;
        this.directionsDisplay = undefined;
        this.waypointsMarker = [];
        // Use for visible flag
        this.isFirstChange = true;
    }
    ngOnInit() {
        if (this.visible === true) {
            this.directionDraw();
        }
    }
    ngOnChanges(obj) {
        /**
         * When visible is false then remove the direction layer
         */
        if (!this.visible) {
            try {
                this.removeMarkers();
                this.removeDirections();
            }
            catch (e) { }
        }
        else {
            if (this.isFirstChange) {
                /**
                 * When visible is false at the first time
                 */
                if (typeof this.directionsDisplay === 'undefined') {
                    this.directionDraw();
                }
                this.isFirstChange = false;
                return;
            }
            /**
             * When renderOptions are not first change then reset the display
             */
            if (typeof obj.renderOptions !== 'undefined') {
                if (obj.renderOptions.firstChange === false) {
                    this.removeMarkers();
                    this.removeDirections();
                }
            }
            this.directionDraw();
        }
    }
    ngOnDestroy() {
        this.destroyMarkers();
        this.removeDirections();
    }
    /**
     * This event is fired when the user creating or updating this direction
     */
    directionDraw() {
        this.gmapsApi.getNativeMap().then((map) => {
            if (typeof this.directionsDisplay === 'undefined') {
                this.directionsDisplay = new google.maps.DirectionsRenderer(this.renderOptions);
                this.directionsDisplay.setMap(map);
                this.directionsDisplay.addListener('directions_changed', () => {
                    this.onChange.emit(this.directionsDisplay.getDirections());
                });
            }
            if (typeof this.directionsService === 'undefined') {
                this.directionsService = new google.maps.DirectionsService;
            }
            if (typeof this.panel === 'undefined') {
                this.directionsDisplay.setPanel(null);
            }
            else {
                this.directionsDisplay.setPanel(this.panel);
            }
            // Render exist direction
            if (typeof this.renderRoute === 'object' && this.renderRoute !== null) {
                this.directionsDisplay.setDirections(this.renderRoute);
                this.renderRoute = null; // or set undefined, ''
            }
            else {
                // Request new direction
                this.directionsService.route({
                    origin: this.origin,
                    destination: this.destination,
                    travelMode: this.travelMode,
                    transitOptions: this.transitOptions,
                    drivingOptions: this.drivingOptions,
                    waypoints: this.waypoints,
                    optimizeWaypoints: this.optimizeWaypoints,
                    provideRouteAlternatives: this.provideRouteAlternatives,
                    avoidHighways: this.avoidHighways,
                    avoidTolls: this.avoidTolls,
                }, (response, status) => {
                    this.onResponse.emit(response);
                    // Emit Query Status
                    this.status.emit(status);
                    /**
                     * DirectionsStatus
                     * https://developers.google.com/maps/documentation/javascript/directions#DirectionsStatus
                     */
                    switch (status) {
                        case 'OK':
                            this.directionsDisplay.setDirections(response);
                            /**
                             * Emit The DirectionsResult Object
                             * https://developers.google.com/maps/documentation/javascript/directions?hl=en#DirectionsResults
                             */
                            // Custom Markers
                            if (typeof this.markerOptions !== 'undefined') {
                                this.destroyMarkers();
                                // Set custom markers
                                const _route = response.routes[0].legs[0];
                                try {
                                    // Origin Marker
                                    if (typeof this.markerOptions.origin !== 'undefined') {
                                        this.markerOptions.origin.map = map;
                                        this.markerOptions.origin.position = _route.start_location;
                                        this.originMarker = this.setMarker(map, this.originMarker, this.markerOptions.origin, _route.start_address);
                                        if (this.markerOptions.origin.draggable) {
                                            this.originMarker.addListener('dragend', () => {
                                                this.origin = this.originMarker.position;
                                                this.directionDraw();
                                                this.originDrag.emit(this.origin);
                                            });
                                        }
                                    }
                                    // Destination Marker
                                    if (typeof this.markerOptions.destination !== 'undefined') {
                                        this.markerOptions.destination.map = map;
                                        this.markerOptions.destination.position = _route.end_location;
                                        this.destinationMarker = this.setMarker(map, this.destinationMarker, this.markerOptions.destination, _route.end_address);
                                        if (this.markerOptions.destination.draggable) {
                                            this.destinationMarker.addListener('dragend', () => {
                                                this.destination = this.destinationMarker.position;
                                                this.directionDraw();
                                                this.destinationDrag.emit(this.destination);
                                            });
                                        }
                                    }
                                    // Waypoints Marker
                                    if (typeof this.markerOptions.waypoints !== 'undefined') {
                                        this.waypoints.forEach((waypoint, index) => {
                                            // If waypoints are not array then set all the same
                                            if (!Array.isArray(this.markerOptions.waypoints)) {
                                                this.markerOptions.waypoints.map = map;
                                                this.markerOptions.waypoints.position = _route.via_waypoints[index];
                                                this.waypointsMarker.push(this.setMarker(map, waypoint, this.markerOptions.waypoints, _route.via_waypoints[index]));
                                            }
                                            else {
                                                this.markerOptions.waypoints[index].map = map;
                                                this.markerOptions.waypoints[index].position = _route.via_waypoints[index];
                                                this.waypointsMarker.push(this.setMarker(map, waypoint, this.markerOptions.waypoints[index], _route.via_waypoints[index]));
                                            }
                                        }); // End forEach
                                    }
                                }
                                catch (err) {
                                    console.error('MarkerOptions error.', err);
                                }
                            }
                            break;
                        default:
                            // console.warn(status);
                            break;
                    } // End switch
                });
            }
        });
    }
    /**
     * Custom Origin and Destination Icon
     * @param map map
     * @param marker marker
     * @param markerOpts properties
     * @param content marker's infowindow content
     * @returns new marker
     * @memberof AgmDirection
     */
    setMarker(map, marker, markerOpts, content) {
        if (typeof this.infoWindow === 'undefined') {
            this.infoWindow = new google.maps.InfoWindow({});
            this.sendInfoWindow.emit(this.infoWindow);
        }
        marker = new google.maps.Marker(markerOpts);
        // https://developers.google.com/maps/documentation/javascript/reference/marker?hl=zh-tw#MarkerOptions.clickable
        if (marker.clickable) {
            marker.addListener('click', () => {
                const infowindoContent = typeof markerOpts.infoWindow === 'undefined' ? content : markerOpts.infoWindow;
                this.infoWindow.setContent(infowindoContent);
                this.infoWindow.open(map, marker);
            });
        }
        return marker;
    }
    /**
     * This event is fired when remove markers
     */
    removeMarkers() {
        if (typeof this.originMarker !== 'undefined') {
            this.originMarker.setMap(null);
        }
        if (typeof this.destinationMarker !== 'undefined') {
            this.destinationMarker.setMap(null);
        }
        this.waypointsMarker.forEach((w) => {
            if (typeof w !== 'undefined') {
                w.setMap(null);
            }
        });
    }
    /**
     * This event is fired when remove directions
     */
    removeDirections() {
        if (this.directionsDisplay !== undefined) {
            this.directionsDisplay.setPanel(null);
            this.directionsDisplay.setMap(null);
            this.directionsDisplay = undefined;
        }
    }
    /**
     * This event is fired when destroy markers
     */
    destroyMarkers() {
        // Remove origin markers
        try {
            if (typeof this.originMarker !== 'undefined') {
                google.maps.event.clearListeners(this.originMarker, 'click');
                if (this.markerOptions.origin.draggable) {
                    google.maps.event.clearListeners(this.originMarker, 'dragend');
                }
            }
            if (typeof this.destinationMarker !== 'undefined') {
                google.maps.event.clearListeners(this.destinationMarker, 'click');
                if (this.markerOptions.origin.draggable) {
                    google.maps.event.clearListeners(this.destinationMarker, 'dragend');
                }
            }
            this.waypointsMarker.forEach((w) => {
                if (typeof w !== 'undefined') {
                    google.maps.event.clearListeners(w, 'click');
                }
            });
            this.removeMarkers();
        }
        catch (err) {
            console.error('Can not reset custom marker.', err);
        }
    }
};
__decorate([
    Input(),
    __metadata("design:type", Object)
], AgmDirection.prototype, "origin", void 0);
__decorate([
    Input(),
    __metadata("design:type", Object)
], AgmDirection.prototype, "destination", void 0);
__decorate([
    Input(),
    __metadata("design:type", String)
], AgmDirection.prototype, "travelMode", void 0);
__decorate([
    Input(),
    __metadata("design:type", Object)
], AgmDirection.prototype, "transitOptions", void 0);
__decorate([
    Input(),
    __metadata("design:type", Object)
], AgmDirection.prototype, "drivingOptions", void 0);
__decorate([
    Input(),
    __metadata("design:type", Object)
], AgmDirection.prototype, "waypoints", void 0);
__decorate([
    Input(),
    __metadata("design:type", Boolean)
], AgmDirection.prototype, "optimizeWaypoints", void 0);
__decorate([
    Input(),
    __metadata("design:type", Boolean)
], AgmDirection.prototype, "provideRouteAlternatives", void 0);
__decorate([
    Input(),
    __metadata("design:type", Boolean)
], AgmDirection.prototype, "avoidHighways", void 0);
__decorate([
    Input(),
    __metadata("design:type", Boolean)
], AgmDirection.prototype, "avoidTolls", void 0);
__decorate([
    Input(),
    __metadata("design:type", Object)
], AgmDirection.prototype, "renderOptions", void 0);
__decorate([
    Input(),
    __metadata("design:type", Object)
], AgmDirection.prototype, "panel", void 0);
__decorate([
    Input(),
    __metadata("design:type", Object)
], AgmDirection.prototype, "markerOptions", void 0);
__decorate([
    Input(),
    __metadata("design:type", Object)
], AgmDirection.prototype, "infoWindow", void 0);
__decorate([
    Input(),
    __metadata("design:type", Boolean)
], AgmDirection.prototype, "visible", void 0);
__decorate([
    Input(),
    __metadata("design:type", Object)
], AgmDirection.prototype, "renderRoute", void 0);
__decorate([
    Output(),
    __metadata("design:type", EventEmitter)
], AgmDirection.prototype, "onChange", void 0);
__decorate([
    Output(),
    __metadata("design:type", EventEmitter)
], AgmDirection.prototype, "onResponse", void 0);
__decorate([
    Output(),
    __metadata("design:type", EventEmitter)
], AgmDirection.prototype, "sendInfoWindow", void 0);
__decorate([
    Output(),
    __metadata("design:type", EventEmitter)
], AgmDirection.prototype, "status", void 0);
__decorate([
    Output(),
    __metadata("design:type", EventEmitter)
], AgmDirection.prototype, "originDrag", void 0);
__decorate([
    Output(),
    __metadata("design:type", EventEmitter)
], AgmDirection.prototype, "destinationDrag", void 0);
AgmDirection = __decorate([
    Directive({
        selector: 'agm-direction',
    }),
    __metadata("design:paramtypes", [GoogleMapsAPIWrapper])
], AgmDirection);

var AgmDirectionModule_1;
let AgmDirectionModule = AgmDirectionModule_1 = class AgmDirectionModule {
    static forRoot() {
        return {
            ngModule: AgmDirectionModule_1,
        };
    }
    static forChild() {
        return {
            ngModule: AgmDirectionModule_1,
        };
    }
};
AgmDirectionModule = AgmDirectionModule_1 = __decorate([
    NgModule({
        declarations: [
            AgmDirection,
        ],
        exports: [
            AgmDirection,
        ]
    })
], AgmDirectionModule);

// Public classes.

/**
 * Angular library starter
 * Build an Angular library compatible with AoT compilation & Tree shaking like an official package
 * Copyright Roberto Simonetti
 * MIT license
 * https://github.com/robisim74/agm-direction
 */
// This file only reexports content of the `src` folder. Keep it that way.

/**
 * Generated bundle index. Do not edit.
 */

export { AgmDirection, AgmDirectionModule };
//# sourceMappingURL=agm-direction.js.map
